import { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2, XCircle, AlertTriangle, CalendarDays, RefreshCw } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";

const API = "https://api.komorotel.com/api/cm-test";

// Valeurs sandbox pré-remplies (hôtel HotelCom / Medina)
const SANDBOX = {
  roomCode: "cmkr3s14i000jn001b66hphyo",       // Chambre Deluxe
  rateplanCode: "cmrktlewl0016n401xykgsuzl",   // Tarif Flexible
};

const field =
  "w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-komoro focus:ring-2 focus:ring-komoro/10 transition";
const label = "text-[11px] font-medium text-gray-400 uppercase tracking-wider";

export default function CMTestPage() {
  const [authKey, setAuthKey] = useState("");
  const [authSecret, setAuthSecret] = useState("");
  const [type, setType] = useState("inventory");
  const [startDate, setStartDate] = useState("2026-10-01");
  const [endDate, setEndDate] = useState("2026-10-03");
  const [roomCode, setRoomCode] = useState(SANDBOX.roomCode);
  const [rateplanCode, setRateplanCode] = useState(SANDBOX.rateplanCode);
  const [available, setAvailable] = useState(2);
  const [stopSell, setStopSell] = useState(false);
  const [rate, setRate] = useState(60);
  const [mealPlan, setMealPlan] = useState("none");
  const [bookingId, setBookingId] = useState("");

  // ── Création de résa test (pour tester le no-show) ──
  const [resaCheckIn, setResaCheckIn] = useState("2026-10-01");
  const [resaCheckOut, setResaCheckOut] = useState("2026-10-03");
  const [resaLoading, setResaLoading] = useState(false);
  const [resaMsg, setResaMsg] = useState("");

  const createResa = async () => {
    setResaLoading(true);
    setResaMsg("");
    try {
      const { data } = await axios.post(`${API}/reservation`, {
        checkInDate: resaCheckIn,
        checkOutDate: resaCheckOut,
        roomCode: SANDBOX.roomCode,
      });
      if (data.ok) {
        setBookingId(data.reservationId);      // pré-remplit le champ no-show
        setResaMsg(`✓ ${data.message}`);
        fetchReservations();                    // affiche la nouvelle résa dans la liste
      } else {
        setResaMsg(`✗ ${data.error}`);
      }
    } catch (err) {
      setResaMsg(`✗ ${err.response?.data?.error || "Erreur réseau."}`);
    } finally {
      setResaLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // ── Calendrier sandbox ──
  const [calStart, setCalStart] = useState("2026-10-01");
  const [calDaysCount] = useState(14);
  const [calData, setCalData] = useState(null);
  const [calLoading, setCalLoading] = useState(false);

  // ── Réservations sandbox ──
  const [resaList, setResaList] = useState(null);
  const [resaListLoading, setResaListLoading] = useState(false);

  const fetchReservations = async () => {
    setResaListLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations`);
      setResaList(data.ok ? data.reservations : []);
    } catch {
      setResaList([]);
    } finally {
      setResaListLoading(false);
    }
  };

  // Liste des jours (YYYY-MM-DD) à afficher, à partir de calStart
  const calDayList = () => {
    const out = [];
    const d0 = new Date(calStart + "T00:00:00Z");
    for (let i = 0; i < calDaysCount; i++) {
      const d = new Date(d0.getTime() + i * 86400000);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  };

  const fetchCalendar = async () => {
    setCalLoading(true);
    try {
      const days = calDayList();
      const start = days[0];
      // getCalendar est exclusif sur endDate -> on passe le lendemain du dernier jour
      const lastPlusOne = new Date(new Date(days[days.length - 1] + "T00:00:00Z").getTime() + 86400000)
        .toISOString().slice(0, 10);
      const { data } = await axios.get(`${API}/calendar`, { params: { startDate: start, endDate: lastPlusOne } });
      setCalData(data.ok ? data.calendar : null);
    } catch {
      setCalData(null);
    } finally {
      setCalLoading(false);
    }
  };

  const buildPayload = () => {
    if (type === "inventory") {
      return { updates: [{ startDate, endDate, rooms: [{ roomCode, available: Number(available), stopSell }] }] };
    }
    if (type === "rates") {
      return { updates: [{ startDate, endDate, rates: [{ roomCode, rateplanCode, rate: Number(rate), mealPlan }] }] };
    }
    // noshow
    return { bookingId };
  };

  const send = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/simulate`, {
        authKey,
        authSecret,
        type,
        payload: buildPayload(),
      });
      setResult(data);
      // Rafraîchit le calendrier après un push inventaire/tarif réussi
      if (data?.ok && data?.authOk && data?.response?.success) {
        if (type === "noshow") fetchReservations();
        else fetchCalendar();
      }
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.error || "Erreur réseau." });
    } finally {
      setLoading(false);
    }
  };

  const authRejected = result?.ok && result?.authOk === false;
  const okResponse = result?.ok && result?.authOk && result?.response?.success;

  return (
    <div className="w-full min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Barre de marque */}
      <header className="w-full bg-komoro">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
          <BrandLogo variant="horizontal" height={30} light={true} />
          <span className="text-white/60 text-xs border-l border-white/20 pl-3">Connectivité PMS</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 flex flex-col gap-5">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Testeur de réception CM</h1>
        <p className="text-sm text-gray-400 mt-0.5">Simule un push HCI → Komorotel (inventaire, tarifs, no-show)</p>
      </div>

      {/* Avertissement sandbox */}
      <div className="flex items-start gap-2 bg-soleil-100 border border-soleil-200 rounded-xl px-3 py-2.5">
        <AlertTriangle size={14} className="text-soleil-700 shrink-0 mt-0.5" />
        <p className="text-xs text-soleil-700">
          Cet outil vise uniquement l'hôtel <b>sandbox</b> (le <span className="font-mono">hotelCode</span> est forcé côté serveur).
          Aucun vrai hôtel ne peut être modifié ici.
        </p>
      </div>

      {/* Formulaire */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-4">

        {/* Credentials Basic Auth à tester */}
        <div className="flex flex-col gap-1">
          <label className={label}>Clé (Basic Auth — username)</label>
          <input type="text" value={authKey} onChange={e => setAuthKey(e.target.value)}
            placeholder="komorotel_..." className={field + " font-mono text-xs"} />
        </div>
        <div className="flex flex-col gap-1">
          <label className={label}>Secret (Basic Auth — password)</label>
          <input type="password" value={authSecret} onChange={e => setAuthSecret(e.target.value)}
            placeholder="votre secret partenaire" className={field + " font-mono text-xs"} />
          <p className="text-[11px] text-gray-400 mt-0.5">
            Ce sont les credentials que vous utilisez pour pousser vers Komorotel. Un mauvais couple = « Invalid credentials », exactement comme en production.
          </p>
        </div>

        {/* Type de push */}
        <div className="flex flex-col gap-1">
          <label className={label}>Type de push</label>
          <div className="flex items-center gap-1 bg-[#f9fafb] border border-gray-200 rounded-xl p-1 w-fit">
            {[
              { key: "inventory", label: "Inventaire" },
              { key: "rates", label: "Tarifs" },
              { key: "noshow", label: "No-show" },
            ].map(t => (
              <button key={t.key} onClick={() => setType(t.key)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${
                  type === t.key ? "bg-komoro text-white" : "text-gray-500 hover:bg-gray-100"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Champs selon le type */}
        {type !== "noshow" ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className={label}>Date début</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={field} />
              </div>
              <div className="flex flex-col gap-1">
                <label className={label}>Date fin (incluse)</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={field} />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className={label}>roomCode (cuid HCI)</label>
              <input value={roomCode} onChange={e => setRoomCode(e.target.value)} className={field + " font-mono text-xs"} />
            </div>

            {type === "rates" && (
              <div className="flex flex-col gap-1">
                <label className={label}>rateplanCode (cuid HCI)</label>
                <input value={rateplanCode} onChange={e => setRateplanCode(e.target.value)} className={field + " font-mono text-xs"} />
              </div>
            )}

            {type === "rates" && (
              <div className="flex flex-col gap-1 max-w-[240px]">
                <label className={label}>Meal plan</label>
                <select value={mealPlan} onChange={e => setMealPlan(e.target.value)} className={field}>
                  <option value="none">Aucun (none)</option>
                  <option value="breakfast">Petit-déjeuner (breakfast)</option>
                  <option value="half_board">Demi-pension (half_board)</option>
                  <option value="full_board">Pension complète (full_board)</option>
                </select>
              </div>
            )}

            <div className="flex items-end gap-4">
              <div className="flex flex-col gap-1 max-w-[200px]">
                <label className={label}>{type === "inventory" ? "Disponibilité" : "Prix (€)"}</label>
                {type === "inventory"
                  ? <input type="number" value={available} onChange={e => setAvailable(e.target.value)} className={field} />
                  : <input type="number" value={rate} onChange={e => setRate(e.target.value)} className={field} />}
              </div>

              {type === "inventory" && (
                <label className="flex items-center gap-2 pb-2 cursor-pointer select-none">
                  <input type="checkbox" checked={stopSell} onChange={e => setStopSell(e.target.checked)}
                    className="w-4 h-4 accent-komoro" />
                  <span className="text-sm text-gray-600">Fermer la chambre (stop-sell)</span>
                </label>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Créer une résa test pour pouvoir tester le no-show dessus */}
            <div className="bg-[#f9fafb] border border-gray-200 rounded-xl p-3 flex flex-col gap-2">
              <p className="text-xs text-gray-500">
                Besoin d'une réservation à cibler ? Crée-en une sur le sandbox (nécessite dispo + prix sur les dates).
              </p>
              <div className="flex items-end gap-2 flex-wrap">
                <div className="flex flex-col gap-1">
                  <label className={label}>Arrivée</label>
                  <input type="date" value={resaCheckIn} onChange={e => setResaCheckIn(e.target.value)}
                    className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-komoro" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={label}>Départ</label>
                  <input type="date" value={resaCheckOut} onChange={e => setResaCheckOut(e.target.value)}
                    className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-komoro" />
                </div>
                <button onClick={createResa} disabled={resaLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-komoro text-white text-xs font-medium rounded-lg hover:bg-komoro-800 transition disabled:opacity-50">
                  {resaLoading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Créer une résa test
                </button>
              </div>
              {resaMsg && <p className="text-[11px] text-gray-600">{resaMsg}</p>}
            </div>

            <div className="flex flex-col gap-1">
              <label className={label}>bookingId (reservationId Komorotel)</label>
              <input value={bookingId} onChange={e => setBookingId(e.target.value)}
                placeholder="KMR-260718-XXXX" className={field + " font-mono text-xs"} />
            </div>
          </div>
        )}

        {/* Bouton */}
        <div className="flex justify-end pt-1">
          <button onClick={send} disabled={loading || !authKey || !authSecret}
            className="flex items-center gap-2 px-5 py-2.5 bg-komoro text-white text-sm font-medium rounded-xl hover:bg-komoro-800 transition disabled:opacity-50">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            Envoyer le push
          </button>
        </div>
      </div>

      {/* Résultat */}
      {result && (
        <div className={`bg-white border rounded-2xl p-5 ${okResponse ? "border-komoro/30" : "border-red-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            {okResponse
              ? <CheckCircle2 size={16} className="text-komoro" />
              : <XCircle size={16} className="text-red-500" />}
            <p className="text-sm font-medium text-gray-900">
              {okResponse
                ? "Authentification OK — push traité avec succès"
                : authRejected
                  ? "Authentification refusée (401) — vérifiez la clé et le secret"
                  : "Authentifié, mais le traitement a renvoyé une erreur"}
            </p>
          </div>

          {/* Badge auth explicite */}
          {result.ok && (
            <p className="text-xs mb-2">
              <span className="text-gray-400">Auth : </span>
              {result.authOk
                ? <span className="text-komoro font-medium">acceptée ✓</span>
                : <span className="text-red-600 font-medium">refusée (401) ✗</span>}
              <span className="text-gray-400"> · HTTP {result.responseStatus}</span>
            </p>
          )}

          {result.error && <p className="text-xs text-red-600 mb-2">{result.error}</p>}

          {result.response && (
            <p className="text-sm text-gray-700 mb-3">
              <span className="text-gray-400">Réponse : </span>
              {result.response.message || JSON.stringify(result.response)}
            </p>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-gray-400 hover:text-gray-600">Détails techniques</summary>
            <pre className="mt-2 p-3 bg-[#f9fafb] rounded-lg overflow-x-auto text-[11px] text-gray-600">
{JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* ── CALENDRIER SANDBOX ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <CalendarDays size={15} className="text-gray-400" />
            <p className="text-sm font-medium text-gray-900">Calendrier du sandbox</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={calStart} onChange={e => setCalStart(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-komoro" />
            <button onClick={fetchCalendar} disabled={calLoading}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-400 disabled:opacity-50">
              <RefreshCw size={14} className={calLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {!calData ? (
          <p className="px-5 py-8 text-center text-xs text-gray-400">
            {calLoading ? "Chargement..." : "Clique sur ↻ pour charger le calendrier (14 jours à partir de la date choisie)."}
          </p>
        ) : calData.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-gray-400">Aucune chambre pour cet hôtel.</p>
        ) : (
          <div className="overflow-x-auto">
            {calData.map(room => (
              <div key={room.roomId} className="border-b border-gray-100 last:border-0">
                <p className="px-5 py-2.5 text-sm font-semibold text-gray-900 bg-[#f9fafb]">{room.title}</p>
                <table className="text-xs">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-400 uppercase sticky left-0 bg-white">Jour</th>
                      {room.days.map(d => (
                        <th key={d.date} className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 whitespace-nowrap">
                          {d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Ligne prix (premier tarif de chaque jour) */}
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-500 sticky left-0 bg-white whitespace-nowrap">Prix</td>
                      {room.days.map(d => (
                        <td key={d.date} className="px-3 py-2 text-center text-gray-800 whitespace-nowrap">
                          {d.rates?.[0] ? `${d.rates[0].price} €` : "—"}
                        </td>
                      ))}
                    </tr>
                    {/* Ligne stock */}
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-500 sticky left-0 bg-white">Stock</td>
                      {room.days.map(d => (
                        <td key={d.date} className="px-3 py-2 text-center text-gray-700">{d.inventory}</td>
                      ))}
                    </tr>
                    {/* Ligne statut */}
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-500 sticky left-0 bg-white">Statut</td>
                      {room.days.map(d => (
                        <td key={d.date} className="px-3 py-2 text-center">
                          <span className={d.status === "opened" ? "text-komoro font-medium" : "text-gray-300"}>
                            {d.status === "opened" ? "O" : "F"}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* ── RÉSERVATIONS SANDBOX ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <CalendarDays size={15} className="text-gray-400" />
            <p className="text-sm font-medium text-gray-900">Réservations du sandbox</p>
          </div>
          <button onClick={fetchReservations} disabled={resaListLoading}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-400 disabled:opacity-50">
            <RefreshCw size={14} className={resaListLoading ? "animate-spin" : ""} />
          </button>
        </div>

        {!resaList ? (
          <p className="px-5 py-8 text-center text-xs text-gray-400">
            {resaListLoading ? "Chargement..." : "Clique sur ↻ pour charger les réservations."}
          </p>
        ) : resaList.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-gray-400">Aucune réservation sur le sandbox.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  {["Réservation", "Client", "Séjour", "Statut"].map(h => (
                    <th key={h} className="px-5 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resaList.map(r => (
                  <tr key={r.reservationId} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-2.5 font-mono text-gray-700 whitespace-nowrap">{r.reservationId}</td>
                    <td className="px-5 py-2.5 text-gray-600 whitespace-nowrap">{r.guest?.firstName} {r.guest?.lastName}</td>
                    <td className="px-5 py-2.5 text-gray-500 whitespace-nowrap">
                      {new Date(r.checkInDate).toLocaleDateString("fr-FR")} → {new Date(r.checkOutDate).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                        r.status === "no_show"   ? "bg-orange-50 text-orange-600" :
                        r.status === "cancelled" ? "bg-red-50 text-red-500" :
                        r.status === "confirmed" ? "bg-emerald-50 text-emerald-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}